const BRIDGE_WAIT_MS = 4000;

/** 만보기(HC/HK) 네이티브 지원 최소 앱 버전 — version.buildVersion */
export const PEDOMETER_MIN_APP_VERSION = 260901.0;

export function parseAppVersion(appInfo) {
  if (!appInfo) return NaN;
  return parseFloat(`${appInfo.version}.${appInfo.buildVersion}`);
}

/** APP에서 260921.00 미만이면 만보기 네이티브 미지원 */
export function isPedometerNativeSupported(appInfo) {
  const version = parseAppVersion(appInfo);
  return Number.isFinite(version) && version >= PEDOMETER_MIN_APP_VERSION;
}

function todayDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function emptyStepsPayload() {
  return {
    /** HC/HK 일일 합계 — 표기·서버 sync·보상 */
    step: 0,
    healthStep: 0,
    displayStep: 0,
    date: todayDate(),
    permission: 'notDetermined',
    source: null,
    /** 최근 7일 Health 걸음 기록 여부 (iOS 건강 앱 사용 추정) */
    hasHealthStepHistory: false,
  };
}

export function formatKm(steps) {
  const safe = Math.max(0, Number(steps) || 0);
  return (safe * 0.6 / 1000).toFixed(2);
}

function toNonNegInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/** HC/HK payload만 사용 (센서 필드 무시) */
export function normalizeStepsPayload(raw) {
  const fallback = emptyStepsPayload();
  if (!raw || typeof raw !== 'object') {
    return fallback;
  }
  const healthStep = toNonNegInt(raw.healthStep ?? raw.step ?? raw.steps);
  return {
    step: healthStep,
    healthStep,
    displayStep: healthStep,
    date: typeof raw.date === 'string' && raw.date ? raw.date : fallback.date,
    permission: typeof raw.permission === 'string' && raw.permission ? raw.permission : fallback.permission,
    source: typeof raw.source === 'string' && raw.source ? raw.source : null,
    hasHealthStepHistory: Boolean(raw.hasHealthStepHistory),
  };
}

function waitForCallHandler() {
  return new Promise((resolve) => {
    if (typeof window.flutter_inappwebview?.callHandler === 'function') {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (ready) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('flutterInAppWebViewPlatformReady', onReady);
      window.clearTimeout(timeoutId);
      resolve(ready);
    };
    const onReady = () => finish(typeof window.flutter_inappwebview?.callHandler === 'function');
    const timeoutId = window.setTimeout(() => finish(false), BRIDGE_WAIT_MS);
    window.addEventListener('flutterInAppWebViewPlatformReady', onReady, { once: true });
  });
}

async function callNative(handlerName) {
  const ready = await waitForCallHandler();
  if (!ready) {
    return emptyStepsPayload();
  }
  try {
    const result = await window.flutter_inappwebview.callHandler(handlerName);
    return normalizeStepsPayload(result);
  } catch (error) {
    console.error(`${handlerName} failed`, error);
    return emptyStepsPayload();
  }
}

export function requestTodaySteps() {
  return callNative('request_step');
}

export function requestStepPermission() {
  return callNative('request_step_permission');
}

/** iOS: 건강 앱 열기 (Android는 no-op일 수 있음) */
export async function openHealthApp() {
  const ready = await waitForCallHandler();
  if (!ready) return false;
  try {
    await window.flutter_inappwebview.callHandler('open_health_app');
    return true;
  } catch (error) {
    console.error('open_health_app failed', error);
    return false;
  }
}

/**
 * iOS: 권한은 있으나 Health에 최근 걸음 기록이 없음
 * → 건강 앱 미사용·동기화 안 됨으로 보고 안내
 */
export function needsIosHealthAppGuide(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.permission !== 'granted') return false;
  if (payload.source !== 'healthkit') return false;
  if (payload.hasHealthStepHistory) return false;
  const step = Number(payload.healthStep ?? payload.step) || 0;
  return step === 0;
}

export function ensureNotifySteps() {
  window.notifySteps = (detail) => {
    window.dispatchEvent(
      new CustomEvent('nativeSteps', {
        detail: detail || {},
      }),
    );
  };
}

export function subscribeNativeSteps(onPayload) {
  ensureNotifySteps();
  const listener = (event) => {
    onPayload(normalizeStepsPayload(event?.detail));
  };
  window.addEventListener('nativeSteps', listener);
  return () => window.removeEventListener('nativeSteps', listener);
}
